import { Injectable } from "@angular/core";
import { Http, Response } from "@angular/http";

import {
    ITCIApplication,
    ITCIApplicationCreateRequest,
    ITCIApplicationUpdateProvider,
    ITCIApplicationUpdateRequest,
    IService,
    MapAppType,
    mapIServiceToTenantAppType,
    QUERY_PARAMS,
    SERVICE_URLS,
    TCI_COMPONENT_MODE,
    TCITenantAppDetailsMenu,
    TCITenantAppType,
    TCI_TENANT_SERVICE_PROVIDER_TYPE
} from "@tci/common";
import { NgAppChangeNotificationService } from "../ng-shared/ng-services/ng-app-change-notification.service";
import { ITCITenantCompCache } from "../ng-tenant/tci-tenant-component-cache.service";
import { TCITenantRemoteServices } from "../ng-tenant/tci-tenant-remote-services";
import { NgAppHMService } from "../ng-app-hamburger-menu/ng-app-hamburger-menu.service";
import { getAppDetailsRoute } from "../ng-shared/ng-util/tools";

import { Observable, BehaviorSubject } from "rxjs";
import { TciTenantModelService } from "../ng-tenant/tci-tenant.model";
import * as lodash from "lodash";

// Note: this interface will be used in *.spec.ts file
export interface IAppMenuOpts {
    appId: string;
    comps: TCITenantAppDetailsMenu[];
}

@Injectable()
export class NgAppListService {
    private static instance: NgAppListService = null;

    private appsCount: number;

    // For initial components build
    private initialCompCache: ITCITenantCompCache;
    private initialCompCacheWork: Observable<ITCITenantCompCache>;

    private resolverAppDetails: ITCIApplication; // Store application data for app-details resolver

    private tagsSavePending$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private availableTenantServices: IService[];

    public static getInstance(
        http: Http,
        tenantRemoteServices: TCITenantRemoteServices,
        appRefreshNotifSvc: NgAppChangeNotificationService,
        hmSvc: NgAppHMService
    ) {
        if (!NgAppListService.instance) {
            NgAppListService.instance = new NgAppListService(
                http,
                tenantRemoteServices,
                appRefreshNotifSvc,
                hmSvc
            );
        }
        return NgAppListService.instance;
    }

    constructor(
        private http: Http,
        private tenantRemoteServices: TCITenantRemoteServices,
        private appRefreshNotifSvc: NgAppChangeNotificationService,
        private hmSvc: NgAppHMService
    ) {
        // This subscriptions should not get unsubscribed as this service is app wide
        appRefreshNotifSvc.getRefreshSubject()
            .subscribe(refr => {
                // Don't update HM options if refresh notification is for app delete.
                if (refr.source && refr.source.provider !== TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_DELETE) {
                    // IMPORTANT: This subscription refreshes the HM options in both GAL rows
                    // and app details, so there's no need to check again in these components
                    this.hmSvc.getAppMenuOptions(refr.app, TCI_COMPONENT_MODE.COMP_MODE_APP_LIST).subscribe();

                    // Update app data for appDetails resolver
                    if (this.resolverAppDetails && this.resolverAppDetails.id === refr.app.id) {
                        this.resolverAppDetails = refr.app;
                    }
                }
            });
    }

    public getApps(): Observable<ITCIApplication[]> { // The array object does not include "info"
        let apps: ITCIApplication[] = [];

        const appsObs = this.http.get(SERVICE_URLS.APPS)
            .map((response: Response) => <ITCIApplication[]>response.json())
            .map(appsData => appsData.map(appData => MapAppType(appData)));

        const tenantsObs = this.tenantRemoteServices.getRemoteServices()
            .flatMap(svcs => {
                this.availableTenantServices = svcs; // Store current tenant services
                return svcs;
            })
            .map(svc => mapIServiceToTenantAppType(svc))
            .distinct(val => val.toString())
            .toArray();

        /**
         * Execute both requests at the same time
         */

        return Observable.combineLatest(appsObs, tenantsObs, (appsData, tenantTypes) => ({
            appsData,
            tenantTypes: tenantTypes
        }))
            .map(data => {
                apps = data.appsData.filter(app => data.tenantTypes.some(srvTenantType => srvTenantType.equals(app.type)));
                this.appsCount = apps.length;
                return apps;
            });
    }

    public getAppById(appId: string, appType?: TCITenantAppType): Observable<ITCIApplication> {
        return this.appRefreshNotifSvc.getAppById(appId, appType)
            .map(res => {
                this.resolverAppDetails = res;
                return res;
            });
    }

    /**
     * For NgAppDetailsModule we need the current application data,
     * so to avoid double-check we store it in a variable to return
     * @param appId
     */
    public getResolverAppData(appId: string, appType?: TCITenantAppType): Observable<ITCIApplication> {
        if (this.resolverAppDetails && this.resolverAppDetails.id === appId) {
            return Observable.of(this.resolverAppDetails);
        } else {
            return this.getAppById(appId, appType);
        }
    }

    public postApp(appInfo: ITCIApplicationCreateRequest): Observable<any> {
        return this.http.post(SERVICE_URLS.APPS, appInfo)
            .map((response: Response) => response.json());
    }

    public getAppName(appType: TCITenantAppType, manifestAppName?: string): Observable<{ name: string }> {
        let queryParam: string = appType.toString() ? `?${QUERY_PARAMS.APP_TYPE}=${appType.toString()}` : "";
        if (manifestAppName) {
            queryParam += `&${QUERY_PARAMS.APP_NAME}=${manifestAppName}`;
        }
        return this.http.get(`${SERVICE_URLS.APPS}/name${queryParam}`)
            .map(response => response.json());
    }

    // App Details Route helper
    public getAppDetailsRoute(app: ITCIApplication): { route: string[], queryParams: Object } {
        return getAppDetailsRoute(app);
    }

    appsExist(): boolean {
        return this.appsCount > 0;
    }

    decreaseAppsCount(): void {
        this.appsCount--;
    }

    // For tags http work
    saveTags(appUpdateProvider: ITCIApplicationUpdateProvider, input: ITCIApplicationUpdateRequest) {
        this.tagsSavePending$.next(true);

        return appUpdateProvider
            .updateApplication(input)
            .map(r => {
                this.tagsSavePending$.next(false);
                return r;
            });
    }

    getTagsSaveSubject() {
        return this.tagsSavePending$;
    }

    /**
     * To avoid more calls to the discovery API, "getApps" sets the
     * tenant services array and it's returned in here if available
     */
    getRemoteServices(): Observable<IService[]> {
        if (this.availableTenantServices) {
            return Observable.of(this.availableTenantServices);
        }
        return this.tenantRemoteServices.getRemoteServices();
    }

    /**
     * rzavala 29 Feb 2020
     * After splitting the tenant's modules into components and services we won't need
     * to pre-compile the HM components. The HM evaluation will happen at the GAL-row
     * component and the actual components will be compiled on-demand on user action
     */
    /* private genInitialComponents(): Observable<ITCITenantCompCache> {
        if (this.initialCompCache) {
            return Observable.of(this.initialCompCache);
        }

        const selectors = [
            // For rows
            TCITenantSelectorType.TCI_APP_LIST_MONITORING_APP_PEEK,
            TCITenantSelectorType.TCI_APP_LIST_MONITORING_APP_PEEK_EXEC,
            TCITenantSelectorType.TCI_APP_DETAIL_ENDPOINTS, // For app peek
            TCITenantSelectorType.TCI_APP_LIST_MINI_DETAIL,

            // For Create App
            TCITenantSelectorType.TCI_APP_CREATE_API_SELECT,
            TCITenantSelectorType.TCI_APP_CREATE_FILE_UPLOAD,
            TCITenantSelectorType.TCI_CREATE_APP
        ];

        const selectorFn = (tenantMap: ITenantMap): TCITenantSelectorType[] => {
            Object.values(tenantMap).forEach(item =>
                selectors.push(
                    ...this.tenantMenuSrv
                        .listAppDetailMenuItems(item.getTenantType(), TCI_APP_DETAIL_ENV_MENU_TYPE.HAMBURGER_MENU)
                        .map(item1 => item1.getComponentSelectorType())
                )
            );
            return lodash.uniqBy(selectors, sel => sel.toString());
        };

        if (!this.initialCompCacheWork) {
            const opts = {
                mainMenu: TCIMainMenu.MAIN_MENU_APP_LIST,
                selectors
            };
            this.initialCompCacheWork = this.tenantSvc.onInit()
                .map(() => this.tenantModelSvc.getTenants())
                .map(res => selectorFn(res))
                .flatMap(res => this.cacheSvc.getCache(false, opts))
                .map(res => {
                    this.initialCompCache = res;
                    return res;
                })
                .share();
        }
        return this.initialCompCacheWork;
    } */
}
